import type { FC } from 'react'

import { Factory, Snowflake, Trees, Warehouse } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/landing/section-heading'
import { industries } from '@/constants/content'

const icons = {
    warehouse: Warehouse,
    snowflake: Snowflake,
    factory: Factory,
    trees: Trees
}

export const Industries: FC = () => {
    return (
        <section id='industries' aria-labelledby='industries-title' className='relative z-10 scroll-mt-16 bg-background py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='industries-title'
                        eyebrow="Industries"
                        title="Built for every floor."
                        body="From freezer aisles to timber yards, the FQ-Series is configured for the work you actually do."
                    />
                    <Reveal delay={120}>
                        <a
                            href='#configure'
                            className='inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-brand hover:text-brand'
                        >
                            Configure for your site
                        </a>
                    </Reveal>
                </div>

                <div className='mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    {industries.map((industry, index) => {
                        const Icon = icons[industry.icon]

                        return (
                            <Reveal key={industry.id} delay={index * 90} className='h-full'>
                                <article className='group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-card p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-brand/40'>
                                    <div
                                        aria-hidden='true'
                                        className='pointer-events-none absolute inset-0 bg-[linear-gradient(oklch(1_0_0/0.035)_1px,transparent_1px),linear-gradient(90deg,oklch(1_0_0/0.035)_1px,transparent_1px)] bg-[size:28px_28px] [mask-image:radial-gradient(120%_80%_at_100%_0%,black,transparent_70%)] opacity-60 transition-opacity duration-500 group-hover:opacity-100 max-sm:opacity-100'
                                    />
                                    <div
                                        aria-hidden='true'
                                        className='pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-brand/10 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 max-sm:opacity-100'
                                    />

                                    <div className='relative flex items-start justify-between'>
                                        <span className='flex size-12 items-center justify-center rounded-2xl border border-brand/25 bg-brand/10 text-brand'>
                                            <Icon className='size-6' />
                                        </span>
                                        <span className='font-mono text-xs text-white/35'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </div>

                                    <h3 className='relative mt-10 font-heading font-expanded text-xl font-bold leading-tight text-white'>
                                        {industry.title}
                                    </h3>
                                    <p className='relative mt-3 flex-1 text-sm leading-relaxed text-white/60'>
                                        {industry.body}
                                    </p>

                                    <div className='relative mt-8 border-t border-white/10 pt-5'>
                                        <p className='font-heading font-expanded text-3xl font-extrabold text-white'>
                                            {industry.stat}
                                        </p>
                                        <p className='mt-1 text-xs text-white/50'>
                                            {industry.statLabel}
                                        </p>
                                    </div>
                                </article>
                            </Reveal>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
